import { NgModule } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import {
  ARROW_DOWN_SVG,
  CLOSE_ICON_SVG,
  DASHES_ICON_SVG,
  DRAG_HANDLER_SVG,
  DRAG_SMALL_SVG,
  FEEDBACK_SVG,
  HELP_SVG,
  HOME_ICON_SVG,
  LOGOUT_ICON_SVG,
  MAGIC_BALL_TOP_MENU_SVG,
  MENU_ICON_SVG,
  MOON_ICON_SVG,
  MY_SETTINGS_ICON_SVG,
  OPEN_IN_NEW_TAB_SVG,
  PIN_ICON_SVG,
  PRINT_SVG,
  ROCKY_ANALYZE_SVG,
  ROCKY_CHAT_SVG,
  SEARCH_ICON_SVG,
  SUN_ICON_SVG,
} from './icon-literals';

@NgModule()
export class IconsModule {
  constructor(
    private _domSanitizer: DomSanitizer,
    private _matIconRegistry: MatIconRegistry,
  ) {
    // Icons that are used in critical places, such as top menu, should be registered as literal icons for better performance
    // Pay attention that that they can significantly increase bundle size
    // To register icon add a tuple with icon name and svg literal to literalIcons array and then use the name to call the icon, for example: <mat-icon svgIcon="close-icon"></mat-icon>
    const literalIcons: Array<[string, string]> = [
      ['close-icon', CLOSE_ICON_SVG],
      ['print', PRINT_SVG],
      ['search-icon', SEARCH_ICON_SVG],
      ['logout_icon', LOGOUT_ICON_SVG],
      ['rocky-chat', ROCKY_CHAT_SVG],
      ['rocky-analyze', ROCKY_ANALYZE_SVG],
      ['magic-ball-top-menu-icon', MAGIC_BALL_TOP_MENU_SVG],
      ['help', HELP_SVG],
      ['open-in-new-tab-icon', OPEN_IN_NEW_TAB_SVG],
      ['menu-icon', MENU_ICON_SVG],
      ['arrow-base_icon', ARROW_DOWN_SVG],
      ['drag-handler_small', DRAG_SMALL_SVG],
      ['drag-handler', DRAG_HANDLER_SVG],
      ['feedback-icon', FEEDBACK_SVG],
      ['home-icon', HOME_ICON_SVG],
      ['my_settings_icon', MY_SETTINGS_ICON_SVG],
      ['pin-icon', PIN_ICON_SVG],
      ['dashes-icon', DASHES_ICON_SVG],

      // Theme switcher icons
      ['sun-icon', SUN_ICON_SVG],
      ['moon-icon', MOON_ICON_SVG],
    ];

    // URL icons, that are loaded as separate files and have worse performance
    // To register icon add a tuple with icon name and url to urlIcons array
    const urlIcons: Array<[string, string]> = [
      ['correlation', '/assets/img/Icons/correlation.svg'],
      ['foundation-readiness', '/assets/img/foundation-readiness-icon.svg'],
      ['heatmap', '/assets/img/Icons/heatmap.svg'],
      ['US_exchange_icon', '/assets/img/exchanges/USA-icon.svg'],
      ['CC_exchange_icon', '/assets/img/exchanges/cryptocurrency-icon.svg'],
      ['CA_exchange_icon', '/assets/img/exchanges/Canada-icon.svg'],
      ['reset', '/assets/img/Icons/reset-icon.svg'],
      ['hint_icon', '/assets/img/Icons/question-mark.svg'],
      ['hint_icon_transparent', '/assets/img/Icons/question-mark-no-color.svg'],
      ['hint_icon_inverted', '/assets/img/Icons/question-mark-inverted-color.svg'],
      ['watch', '/assets/img/Icons/watch.svg'],
      ['plus_icon', '/assets/img/Icons/plus.svg'],
      ['search-tool_icon', '/assets/img/Icons/search-no-color.svg'],
      ['arrow-left_icon', '/assets/img/Icons/arrow-left.svg'],
      ['plus', '/assets/img/Icons/plus-icon.svg'],
      ['plus_large', '/assets/img/Icons/large_plus.svg'],
      ['memories-brain', '/assets/img/Icons/memories-brain.svg'],
      ['select_icon', '/assets/img/Icons/select-icon.svg'],
      ['edit_icon', '/assets/img/Icons/edit-icon.svg'],
      ['trash_icon', '/assets/img/Icons/trash-icon.svg'],
      ['statistic_icon', '/assets/img/Icons/statistic.svg'],
      ['archive_icon_out', '/assets/img/Icons/archive-out.svg'],
      ['archive_icon_in', '/assets/img/Icons/archive-in.svg'],
      ['dots_icon', '/assets/img/Icons/dots.svg'],
      ['move_icon', '/assets/img/Icons/move.svg'],
      ['remove_icon', '/assets/img/Icons/delete.svg'],
      ['attention_icon', '/assets/img/Icons/attention_icon.svg'],
      ['print-grey', '/assets/img/Icons/print-grey.svg'],
      ['warning', '/assets/img/Icons/warning-sign.svg'],
      ['news-icon', '/assets/img/Icons/news-icon.svg'],
      ['calendar-icon', '/assets/img/Icons/calendar-today.svg'],
      ['calendar-alternate-icon', '/assets/img/Icons/calendar-alternate.svg'],
      ['calendar-small-icon', '/assets/img/Icons/calendar-small-icon.svg'],
      ['arrow-nav-icon', '/assets/img/Icons/arrow.svg'],
      ['community', '/assets/img/Icons/community.svg'],
      ['tutorials', '/assets/img/Icons/tutorials.svg'],
      ['arrow-icon', '/assets/img/Icons/arrow-icon.svg'],
      ['arrow-sm-icon', '/assets/img/Icons/arrow-sm.svg'],
      ['success', '/assets/img/alert-success.svg'],
      ['tabs-mode-icon', '/assets/img/Icons/tabs-mode-icon.svg'],
      ['split-mode-icon', '/assets/img/Icons/split-mode-icon.svg'],
      ['menu', '/assets/img/menu-icon/menu.svg'],
      ['header-settings-v2', '/assets/img/newImg/header-setting.svg'],
      ['config-icon', '/assets/img/menu-icon/config.svg'],
      ['settings-icon', '/assets/img/menu-icon/Setting.svg'],
      ['arrow', '/assets/img/arrow-down.svg'],
      ['flag-us', '/assets/img/countries/flag.svg'], // remove it later
      ['time-icon', '/assets/img/Icons/time.svg'],
      ['sync-ok', '/assets/img/Icons/sync-ok.svg'],
      ['sync-in-progress', '/assets/img/Icons/sync-in-progress.svg'],
      ['sync-error', '/assets/img/Icons/sync-error.svg'],
      ['success-icon', '/assets/img/Icons/success-icon.svg'],
      ['danger-icon', '/assets/img/Icons/danger-icon.svg'],
      ['alert-error', '/assets/img/alert-danger.svg'],
      ['alert-success', '/assets/img/Icons/Success.svg'],
      ['filter-icon', '/assets/img/Icons/filter-settings.svg'],
      ['filters-icon', '/assets/img/Icons/filters.svg'],
      ['refresh-update-icon', '/assets/img/Icons/refresh-update-icon.svg'],
      ['refresh-update-icon-v2', '/assets/img/Icons/refresh-update-icon-v2.svg'],
      ['risk-icon', '/assets/img/Icons/risk-icon.svg'],
      ['analyze-earning-icon', '/assets/img/Icons/analyze-earning-icon.svg'],
      ['long-arrow-icon', '/assets/img/Icons/long-arrow.svg'],
      ['prev-arrow-icon', '/assets/img/Icons/prev-arrow.svg'],
      ['next-arrow-icon', '/assets/img/Icons/next-arrow.svg'],
      ['warning-icon', '/assets/img/Icons/warning.svg'],
      ['close-thick', '/assets/img/Icons/delete-icon.svg'],
      ['lock-opened', '/assets/img/Icons/lock-opened.svg'],
      ['lock-closed', '/assets/img/Icons/lock-closed.svg'],
      ['eye', '/assets/img/Icons/eye.svg'],
      ['close-eye', '/assets/img/Icons/close-eye.svg'],
      ['show-on-chart', '/assets/img/Icons/show-on-chart.svg'],
      ['gray-smile', '/assets/img/GreySmile-current-color.svg'],
      ['red-smile', '/assets/img/RedSmile.svg'],
      ['yellow-smile', '/assets/img/YellowSmile.svg'],
      ['green-smile', '/assets/img/GreenSmile.svg'],
      ['never-smile', '/assets/img/NeverSmile-current-color.svg'],
      ['emotions-none-icon', '/assets/img/smiley-icons/emotions-none-v2.svg'],
      ['emotions-maybe-icon', '/assets/img/smiley-icons/emotions-maybe-v2.svg'],
      ['emotions-yes-icon', '/assets/img/smiley-icons/emotions-yes-v2.svg'],
      ['emotions-no-icon', '/assets/img/smiley-icons/emotions-no-v2.svg'],
      ['emotions-never-icon', '/assets/img/smiley-icons/emotions-never-v2.svg'],
      ['pre-market', '/assets/img/Icons/pre-market.svg'],
      ['post-market', '/assets/img/Icons/post-market.svg'],
      ['person', '/assets/img/Icons/person.svg'],
      ['play-icon', '/assets/img/Icons/play.svg'],
      ['info-icon', '/assets/img/Icons/info-icon.svg'],
      ['magic-ball-icon', '/assets/img/Icons/magic-ball-icon.svg'],

      // Navigation and menu
      ['pie_chart_icon', '/assets/img/menu-icon/pie_chart.svg'],
      ['bubble_chart_icon', '/assets/img/menu-icon/bubble_chart.svg'],
      ['info_icon', '/assets/img/menu-icon/info.svg'],
      ['calendar_today_icon', '/assets/img/menu-icon/calendar_today.svg'],
      ['trading_panel_icon', '/assets/img/menu-icon/trading-panel.svg'],
      ['hot_list_icon', '/assets/img/menu-icon/hot-list.svg'],
      ['short-selling-stocks-icon', '/assets/img/menu-icon/short-selling-stocks.svg'],
      ['wb_sunny_icon', '/assets/img/menu-icon/wb_sunny.svg'],
      ['table_chart_icon', '/assets/img/menu-icon/table_chart.svg'],
      ['economic-calendar', '/assets/img/Icons/economic-calendar.svg'],
      ['earnings-calendar', '/assets/img/Icons/earnings-calendar.svg'],
      ['dividends-calendar', '/assets/img/Icons/dividends-calendar.svg'],
      ['star-icon', '/assets/img/menu-icon/star-icon.svg'],
      ['theme_toggle_icon', '/assets/img/menu-icon/theme_toggle.svg'],
      ['side_nav_close', '/assets/img/Icons/side-nav-close.svg'],
      ['drop_down_arrow', '/assets/img/Icons/drop-down-arrow.svg'],
      ['alert-disabled', '/assets/img/Icons/disable.svg'],
      ['hint_filled_icon', '/assets/img/Icons/help_filled.svg'],
      ['chat-gpt', '/assets/img/Icons/chat-gpt.svg'],
      ['like', '/assets/img/Icons/like.svg'],
      ['dislike', '/assets/img/Icons/dislike.svg'],
      ['limit-icon', '/assets/img/Icons/limit-icon.svg'],
      ['reply-icon', '/assets/img/Icons/reply-icon.svg'],
      ['send-icon', '/assets/img/Icons/send-icon.svg'],
      ['regenerate-icon', '/assets/img/Icons/regenerate-icon.svg'],
      ['pagination-arrow-left-icon', '/assets/img/Icons/pagination-arrow-left.svg'],
      ['pagination-arrow-right-icon', '/assets/img/Icons/pagination-arrow-right.svg'],
      ['reset-icon', '/assets/img/Icons/reset-narrow.svg'],
      ['tree-icon', '/assets/img/Icons/tree-view.svg'],
      ['list-icon', '/assets/img/Icons/list-view.svg'],
      ['help-icon', '/assets/img/Icons/help.svg'],
      ['table-view-icon', '/assets/img/Icons/table.svg'],
      ['graph-view-icon', '/assets/img/Icons/graph.svg'],
      ['sort_icon', '/assets/img/Icons/sort.svg'],
      ['refresh-small', '/assets/img/Icons/refresh-small.svg'],
      ['breadcrumbs-icon-all', '/assets/img/Icons/settings.svg'],
      ['outer-link', '/assets/img/Icons/outer-link.svg'],
      ['columns_icon', '/assets/img/Icons/columns.svg'],
      ['export-table', '/assets/img/Icons/export-table.svg'],
      ['rows-view-mode', '/assets/img/Icons/rows-view-mode.svg'],
      ['columns-view-mode', '/assets/img/Icons/columns-view-mode.svg'],
      ['contact_support', '/assets/img/Icons/contact_support.svg'],
      ['info', '/assets/img/Icons/info.svg'],
      ['arrow_upward', '/assets/img/Icons/arrow_upward.svg'],
      ['arrow_downward', '/assets/img/Icons/arrow_downward.svg'],
      ['two_arrows_up_down', '/assets/img/Icons/two_arrows_up_down.svg'],
      ['inspiration-icon', '/assets/img/Icons/inspiration.svg'],
      ['prev-slide-icon', '/assets/img/Icons/prev-slide-icon.svg'],
      ['next-slide-icon', '/assets/img/Icons/next-slide-icon.svg'],
      ['import-trades-icon', '/assets/img/Icons/import-trades.svg'],
      ['uploading-icon', '/assets/img/Icons/uploading-icon.svg'],
      ['complete-icon', '/assets/img/Icons/complete-icon.svg'],
      ['save_icon', '/assets/img/Icons/icon_save.svg'],
      ['add-section', '/assets/img/Icons/add-section.svg'],
      ['break-even-warning-icon', '/assets/img/Icons/break-even-warning-sign.svg'],
      ['yt_logo', '/assets/img/Icons/youtube_icon.svg'],
      ['rocky-icon', '/assets/img/Icons/rocky-icon.svg'],
      ['data-window-icon', '/assets/img/Icons/data-window-icon.svg'],
      ['heatmap-share-icon', '/assets/img/Icons/heatmap-share-icon.svg'],
      ['help-hint', '/assets/img/Icons/help-current-color-hint.svg'],
      ['live-stream-icon', '/assets/img/Icons/live-stream.svg'],
      ['background-video-icon', '/assets/img/Icons/background-window-icon.svg'],
      ['separate-video-icon', '/assets/img/Icons/separate-window-icon.svg'],
      ['cards_view_icon', '/assets/img/Icons/cards-view.svg'],
      ['list_view_icon', '/assets/img/Icons/list-view-2.svg'],
      ['expand-video-icon', '/assets/img/Icons/expand_view.svg'],
      ['double-arrow-up-icon', '/assets/img/Icons/double-arrow-up.svg'],
      ['double-arrow-down-icon', '/assets/img/Icons/double-arrow-down.svg'],
      ['icon-line-through', '/assets/img/Icons/icon-line-through.svg'],
      ['star-outline-icon', '/assets/img/Icons/star-icon-outlined.svg'],
      ['trend-alert', '/assets/img/Icons/trend_alert.svg'],
      ['trend-up', '/assets/img/Icons/trend_up.svg'],
      ['trend-down', '/assets/img/Icons/trend_down.svg'],
      ['insert_row_above', '/assets/img/mm-trades/insert-row-above.svg'],
      ['insert_row_below', '/assets/img/mm-trades/insert-row-below.svg'],
      ['video_hub_icon', '/assets/img/Icons/video-hub-icon.svg'],
      ['video_hub_replay_icon', '/assets/img/Icons/video-hub-replay-icon.svg'],
      ['chart_avg_icon', '/assets/img/Icons/chart_avg_icon.svg'],
      ['undo-arrow-icon', '/assets/img/Icons/undo-arrow-icon.svg'],
      ['redo-arrow-icon', '/assets/img/Icons/redo-arrow-icon.svg'],
      ['tl-quotes-icon', '/assets/img/Icons/tl-quotes-icon.svg'],
      ['notes-icon', '/assets/img/Icons/notes-icon.svg'],
      ['rocky-chat-btn', '/assets/img/Icons/rocky.svg'],
      ['arrow-up-circle-icon', '/assets/img/Icons/arrow-up-icon.svg'],
      ['notifications-icon', '/assets/img/Icons/notifications-icon.svg'],
      ['set-alert-icon', '/assets/img/Icons/set-alert-icon.svg'],

      // Top menu icons
      ['power-x-icon', '/assets/img/Icons/power-x-icon-v2.svg'],
      ['wheel-icon', '/assets/img/Icons/wheel-icon-v2.svg'],
      ['bcs-icon', '/assets/img/Icons/bcs-icon-v2.svg'],
      ['wtf-icon', '/assets/img/Icons/wtf-icon-v2.svg'],
      ['sol-icon', '/assets/img/Icons/sol-icon-v2.svg'],
      ['system-one-icon', '/assets/img/Icons/system-one-icon-v2.svg'],
      ['dividends-icon', '/assets/img/Icons/dividends-icon-v2.svg'],
      ['m-and-m-strategy-icon', '/assets/img/Icons/m-and-m-strategy-icon.svg'],
      ['rob-x-strategy-icon', '/assets/img/Icons/rob-x-strategy-icon.svg'],
      ['rocky-strategy-icon', '/assets/img/Icons/rocky-strategy-icon.svg'],
      ['alerts-service-icon', '/assets/img/Icons/alerts-service-icon.svg'],
      ['tax-harvesting', '/assets/img/Icons/tax-harvesting-icon.svg'],
      ['dividends-tracker', '/assets/img/Icons/dividends-tracker-icon.svg'],
      ['sp-500-dashboard', '/assets/img/Icons/sp-500-dashboard-icon.svg'],
      ['portfolio-builder', '/assets/img/Icons/portfolio-builder-icon.svg'],
      ['bounding-box', '/assets/img/Icons/bounding-box.svg'],
      ['passive-scanner', '/assets/img/Icons/passive-scanner.svg'],

      // Social media icons
      ['apple-icon', '/assets/img/Icons/social-media-icons/apple-icon.svg'],
      ['facebook-messenger-icon', '/assets/img/Icons/social-media-icons/facebook-messenger-icon.svg'],
      ['google-business-messages-icon', '/assets/img/Icons/social-media-icons/google-business-messages-icon.svg'],
      ['instagram-icon', '/assets/img/Icons/social-media-icons/instagram-icon.svg'],
      ['line-icon', '/assets/img/Icons/social-media-icons/line-icon.svg'],
      ['rcs-icon', '/assets/img/Icons/social-media-icons/rcs-icon.svg'],
      ['telegram-icon', '/assets/img/Icons/social-media-icons/telegram-icon.svg'],
      ['viber-icon', '/assets/img/Icons/social-media-icons/viber-icon.svg'],
      ['round-sms-icon', '/assets/img/Icons/social-media-icons/round-sms-icon.svg'],

      ['green-checkmark-icon', '/assets/img/Icons/green-checkmark-icon.svg'],
      ['not-verified-phone-icon', '/assets/img/Icons/not-verified-phone-icon.svg'],
      ['verified-phone-icon', '/assets/img/Icons/verified-phone-icon.svg'],
      ['remove-icon', '/assets/img/Icons/remove-icon.svg'],
      ['pxo-icon', '/assets/img/Icons/pxo-icon.svg'],
      ['dividends', '/assets/img/Icons/dividends.svg'],
      ['lock-icon', '/assets/img/Icons/editions-lock-icon.svg'],
      ['move-icon', '/assets/img/Icons/move-icon.svg'],
      ['refresh-data', '/assets/img/refresh-background-light.svg'],
      ['process-data', '/assets/img/process-background-icon.svg'],
      ['question-mark-wheel', '/assets/img/Icons/question-mark.svg'],
      ['stash-last-updates-duotone', '/assets/img/Icons/stash-last-updates-duotone-v2.svg'],
      ['stash-last-updates-duotone-second', '/assets/img/Icons/stash-last-updates-duotone-second-v2.svg'],
      ['email-icon', '/assets/img/Icons/alerts-notifications-icons/email-icon.svg'],
      ['whatsapp-icon', '/assets/img/Icons/alerts-notifications-icons/whatsapp-icon.svg'],
      ['sms-icon', '/assets/img/Icons/alerts-notifications-icons/sms-icon.svg'],
      ['as-telegram-icon', '/assets/img/Icons/alerts-notifications-icons/telegram-icon.svg'],
      ['website-popup-icon', '/assets/img/Icons/alerts-notifications-icons/website-popup-icon.svg'],
      ['high-trend-arrow-icon', '/assets/img/Icons/high-trend-arrow.svg'],
      ['medium-trend-arrow-icon', '/assets/img/Icons/medium-trend-arrow.svg'],
      ['low-trend-arrow-icon', '/assets/img/Icons/low-trend-arrow.svg'],
      ['non-trend-arrow-icon', '/assets/img/Icons/non-trend-arrow.svg'],

      // ----- Symbol tabs menu icons start ----- //
      ['menu-chart', '/assets/img/Icons/symbol-tab-icons/menu-chart.svg'],
      ['menu-report', '/assets/img/Icons/symbol-tab-icons/menu-report.svg'],
      ['menu-options', '/assets/img/Icons/symbol-tab-icons/menu-options.svg'],
      ['menu-technical', '/assets/img/Icons/symbol-tab-icons/menu-technical.svg'],
      ['menu-analyst', '/assets/img/Icons/symbol-tab-icons/menu-analyst.svg'],
      ['menu-dividends', '/assets/img/Icons/symbol-tab-icons/menu-divs.svg'],
      ['menu-divs', '/assets/img/Icons/symbol-tab-icons/menu-divs.svg'],
      ['menu-insider', '/assets/img/Icons/symbol-tab-icons/menu-insider.svg'],
      ['menu-earnings', '/assets/img/Icons/symbol-tab-icons/menu-earnings.svg'],
      ['menu-fundies', '/assets/img/Icons/symbol-tab-icons/menu-fundies.svg'],
      ['menu-news', '/assets/img/Icons/symbol-tab-icons/menu-news.svg'],
      ['menu-calc', '/assets/img/Icons/symbol-tab-icons/menu-calculator.svg'],
      ['menu-opt-chain', '/assets/img/Icons/symbol-tab-icons/menu-opt-chain.svg'],
      ['menu-roll', '/assets/img/Icons/symbol-tab-icons/menu-roll.svg'],
      ['menu-edge-finder', '/assets/img/Icons/symbol-tab-icons/menu-edge-finder.svg'],
      // ----- Symbol tabs menu icons end ----- //

      ['bulb-icon-blue', '/assets/img/Icons/bulb-icon-blue.svg'],
      ['clock-icon', '/assets/img/Icons/clock-icon.svg'],
      ['hand-up', '/assets/img/Icons/hand-up.svg'],
      ['hand-down', '/assets/img/Icons/hand-down.svg'],
      ['shrink-video-icon', '/assets/img/Icons/shrink-video-icon.svg'],
      ['rocky-chat-grey', '/assets/img/Icons/rocky-chat-grey.svg'],
      ['plus-transparent', '/assets/img/Icons/plus-transparent.svg'],
      ['speaker-icon', '/assets/img/Icons/speaker-icon-no-color.svg'],
      ['copy-icon', '/assets/img/Icons/copy-icon-no-color.svg'],
      ['copy-icon-detailed', '/assets/img/Icons/copy-icon-detailed.svg'],
      ['success-check-icon', '/assets/img/Icons/success-check-icon-no-color.svg'],
      ['stop-button-icon-red', '/assets/img/Icons/stop-button-icon-red.svg'],
      ['stop-button-icon', '/assets/img/Icons/stop-button-icon-no-color.svg'],
      ['income-machine', '/assets/img/Icons/income-calculator-icon-no-color.svg'],
      ['winning-strategy-selector', '/assets/img/Icons/winning-strategy-selector-icon-no-color.svg'],
      ['sources-icon', '/assets/img/Icons/sources-icon-no-color.svg'],
      ['hide-sources-icon', '/assets/img/Icons/hide-sources-icon-no-color.svg'],
      ['fire-blue', '/assets/img/Icons/fire-icon-blue.svg'],
      ['dictate-icon', '/assets/img/Icons/dictate-icon-no-color.svg'],
      ['exclamation-mark', '/assets/img/Icons/exclamation-mark.svg'],
      ['exclamation-mark-v2', '/assets/img/Icons/exclamation-mark-v2.svg'],
      ['ok-circle', '/assets/img/Icons/ok-green-circle.svg'],
      ['thinking', '/assets/img/Icons/thinking-icon.svg'],
      ['collecting', '/assets/img/Icons/collecting-icon.svg'],
      ['rocky-news', '/assets/img/Icons/rocky-ai-white-rounded-bottom.svg'],
      ['clear-data', '/assets/img/Icons/clear-no-color.svg'],
      ['thumbs-down', '/assets/img/Icons/thumbs-down.svg'],
      ['thumbs-up', '/assets/img/Icons/thumbs-up.svg'],
      ['cannoli', '/assets/img/Icons/holy-cannoli.svg'],
      ['cannoli-active', '/assets/img/Icons/holy-cannoli-colored.svg'],
      ['cannoli-rays', '/assets/img/Icons/holy-cannoli-rays.svg'],
      ['rescue', '/assets/img/Icons/rescue.svg'],
      ['clock-regular', '/assets/img/Icons/clock_regular.svg'],
      ['clock-extended', '/assets/img/Icons/clock_extended.svg'],
      ['4-wheeler_calculator', '/assets/img/Icons/4-wheeler_earnings_wheeler_calculator.svg'],
      ['trading_style_profile', '/assets/img/Icons/4-wheeler_earnings_wheeler_calculator.svg'],

      // AI chat icons
      ['chat-upload', '/assets/img/Icons/pxo-ai-chat-icons/chat-upload.svg'],
      ['dropzone', '/assets/img/Icons/pxo-ai-chat-icons/dropzone.svg'],
      ['doc-preview', '/assets/img/Icons/pxo-ai-chat-icons/doc-preview.svg'],
      ['image-preview', '/assets/img/Icons/pxo-ai-chat-icons/image-preview.svg'],
      ['table-preview', '/assets/img/Icons/pxo-ai-chat-icons/table-preview.svg'],
      ['stars', '/assets/img/Icons/pxo-ai-chat-icons/stars.svg'],
      ['explore-icon', '/assets/img/Icons/pxo-ai-chat-icons/explore-icon.svg'],
      ['checklist', '/assets/img/Icons/pxo-ai-chat-icons/checklist.svg'],
      ['bag', '/assets/img/Icons/pxo-ai-chat-icons/bag.svg'],
      ['chart-schematic', '/assets/img/Icons/pxo-ai-chat-icons/chart-schematic.svg'],
      ['file-checked', '/assets/img/Icons/pxo-ai-chat-icons/file-checked.svg'],
      ['compass', '/assets/img/Icons/pxo-ai-chat-icons/compass.svg'],

      // Wealth OS icons
      ['financial-snapshot', '/assets/img/Icons/wealth-os/financial-snapshot.svg'],
      ['camera-icon', '/assets/img/Icons/wealth-os/camera-icon.svg'],
      ['target-circle', '/assets/img/Icons/wealth-os/target-circle-icon.svg'],
      ['chart-arrow-up', '/assets/img/Icons/wealth-os/chart-arrow-up.svg'],
      ['usd-icon', '/assets/img/Icons/wealth-os/usd-icon.svg'],
      ['property-icon', '/assets/img/Icons/wealth-os/property-icon.svg'],
      ['papers-icon', '/assets/img/Icons/wealth-os/papers-icon.svg'],
      ['credit-card', '/assets/img/Icons/wealth-os/credit-card-icon.svg'],
      ['plus-no-color', '/assets/img/Icons/plus-no-color.svg'],
      ['budget-logo', '/assets/img/Icons/wealth-os/budget-logo.svg'],
      ['usd-bulb-icon', '/assets/img/Icons/wealth-os/usd-bulb-icon-orange.svg'],
      ['warning-triangle', '/assets/img/Icons/wealth-os/warning-triangle.svg'],
      ['zap-icon-green', '/assets/img/Icons/wealth-os/zap-icon-green.svg'],
      ['schedule-icon', '/assets/img/Icons/wealth-os/schedule-icon-blue.svg'],
      ['circle-icon', '/assets/img/Icons/wealth-os/circle-icon.svg'],
      ['housing-category-icon', '/assets/img/Icons/wealth-os/housing-icon.svg'],
      ['transportation-category-icon', '/assets/img/Icons/wealth-os/transport-icon.svg'],
      ['food-category-icon', '/assets/img/Icons/wealth-os/grocery-icon.svg'],
      ['healthcare-category-icon', '/assets/img/Icons/wealth-os/cardio-icon.svg'],
      ['utilities-category-icon', '/assets/img/Icons/wealth-os/utilities-icon.svg'],
      ['dining-category-icon', '/assets/img/Icons/wealth-os/dining-icon.svg'],
      ['entertainment-category-icon', '/assets/img/Icons/wealth-os/entertainment-icon.svg'],
      ['shopping-category-icon', '/assets/img/Icons/wealth-os/shopping-icon.svg'],
      ['subscriptions-category-icon', '/assets/img/Icons/wealth-os/subscriptions-icon.svg'],
      ['travel-category-icon', '/assets/img/Icons/wealth-os/travel-icon.svg'],
      ['emergency-category-icon', '/assets/img/Icons/wealth-os/emergency-funds-icon.svg'],
      ['retirement-category-icon', '/assets/img/Icons/wealth-os/retirement-icon.svg'],
      ['investments-category-icon', '/assets/img/Icons/wealth-os/investment-icon.svg'],
      ['debt-category-icon', '/assets/img/Icons/wealth-os/debt-icon.svg'],
      ['chart-donut', '/assets/img/Icons/wealth-os/chart-donut.svg'],
      ['chart-line-up', '/assets/img/Icons/wealth-os/chart-line-up.svg'],
      ['squares-four', '/assets/img/Icons/wealth-os/squares-four.svg'],
      ['terminal-window', '/assets/img/Icons/wealth-os/terminal-window.svg'],
      ['paper-plane', '/assets/img/Icons/wealth-os/paper-plane.svg'],
      ['tip-jar-icon', '/assets/img/Icons/wealth-os/tip-jar.svg'],
      ['liquid-icon', '/assets/img/Icons/wealth-os/water-drop.svg'],
      ['database-icon', '/assets/img/Icons/wealth-os/database-icon.svg'],
      ['pie-chart-slice', '/assets/img/Icons/wealth-os/chart-pie-slice.svg'],
      ['file-uploading', '/assets/img/Icons/wealth-os/file-uploading.svg'],
      ['attention-mark', '/assets/img/Icons/wealth-os/attention-mark.svg'],
      ['trophy', '/assets/img/Icons/wealth-os/trophy.svg'],
      ['add-in-circle', '/assets/img/Icons/wealth-os/add-circle.svg'],
      ['institution', '/assets/img/Icons/wealth-os/institution.svg'],
      ['car-icon', '/assets/img/Icons/wealth-os/car-icon.svg'],
      ['house-icon', '/assets/img/Icons/wealth-os/house-icon.svg'],
      ['bank-icon', '/assets/img/Icons/wealth-os/bank-icon.svg'],
      ['connector', '/assets/img/Icons/wealth-os/connector.svg'],
      ['lock', '/assets/img/Icons/wealth-os/lock.svg'],
      ['guard', '/assets/img/Icons/wealth-os/guard.svg'],
      ['address-pointer', '/assets/img/Icons/wealth-os/address-pointer.svg'],
      ['no-data-ba-chart', '/assets/img/no-data-images/no-data-ba-chart-light.svg'],
      ['mileage', '/assets/img/Icons/wealth-os/mileage.svg'],
      ['vin-mark', '/assets/img/Icons/wealth-os/vin-mark.svg'],
      ['repair-icon', '/assets/img/Icons/wealth-os/repair-icon.svg'],
      ['strong-icon', '/assets/img/Icons/wealth-os/strong-icon.svg'],
      ['party-icon', '/assets/img/Icons/wealth-os/party-icon.svg'],
      ['broker-icon', '/assets/img/Icons/wealth-os/broker-icon.svg'],
      ['rocket-icon', '/assets/img/Icons/wealth-os/rocket-icon.svg'],
      ['ok-rounded', '/assets/img/Icons/wealth-os/ok-rounded.svg'],
      ['cross-rounded', '/assets/img/Icons/wealth-os/cross-rounded.svg'],
      ['usd-in-circle', '/assets/img/Icons/wealth-os/usd-in-circle.svg'],
      ['guard-check', '/assets/img/Icons/wealth-os/guard-check.svg'],
      ['education', '/assets/img/Icons/wealth-os/education.svg'],

      ['upload-img-icon', '/assets/img/Icons/upload-img-icon.svg'],
      ['upload-file-icon', '/assets/img/Icons/upload-file-icon.svg'],
      ['icon-without-border-left', '/assets/img/Icons/icon-without-border-left.svg'],
      ['icon-without-border-right', '/assets/img/Icons/icon-without-border-right.svg'],
      ['zoom-in-icon', '/assets/img/Icons/zoom-in-icon.svg'],
      ['zoom-out-icon', '/assets/img/Icons/zoom-out-icon.svg'],
      ['expand-icon', '/assets/img/Icons/expand-icon.svg'],
      ['money-bag', '/assets/img/Icons/money-bag.svg'],
      ['ring-icon', '/assets/img/Icons/ring-icon.svg'],
      ['handshake', '/assets/img/Icons/handshake.svg'],
      ['guard-question', '/assets/img/Icons/guard-question.svg'],
      ['self', '/assets/img/Icons/self.svg'],
      ['children', '/assets/img/Icons/children.svg'],
      ['trader_icon', '/assets/img/menu-icon/trader.svg'],
      ['m_m_trader_icon', '/assets/img/menu-icon/m-m-trader.svg'],
      ['plus-squared', '/assets/img/Icons/plus-squared.svg'],
      ['install-mobile', '/assets/img/Icons/install-mobile.svg'],
      ['download-icon', '/assets/img/Icons/download-icon.svg'],
      ['ok-icon', '/assets/img/Icons/ok-icon.svg'],
      ['brackets-plus', '/assets/img/Icons/brackets-plus.svg'],
      ['brackets-minus', '/assets/img/Icons/brackets-minus.svg'],
      ['play-circle', '/assets/img/Icons/play-circle.svg'],
      ['rotate-mobile', '/assets/img/Icons/rotate-mobile.svg'],
      ['scheduled-tasks', '/assets/img/Icons/scheduled-tasks.svg'],
      ['view-icon', '/assets/img/Icons/view-icon.svg'],
    ];

    literalIcons.forEach(([name, svg]) =>
      this._matIconRegistry.addSvgIconLiteral(name, this._domSanitizer.bypassSecurityTrustHtml(svg)),
    );

    urlIcons.forEach(([name, url]) =>
      this._matIconRegistry.addSvgIcon(name, this._domSanitizer.bypassSecurityTrustResourceUrl(url)),
    );
  }
}
